import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getTeamMembers, createTeamMember, deleteTeamMember, updateUserRole } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Users, Plus, Trash2, Loader2, Shield, User, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export const AdminModal = ({ open, onClose }) => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', password: '', role: 'user' });

  useEffect(() => {
    if (open) {
      fetchMembers();
    }
  }, [open]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await getTeamMembers();
      setMembers(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        onClose();
      } else {
        toast.error('Failed to load team members');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newMember.name || !newMember.email || !newMember.password) {
      toast.error('All fields are required');
      return;
    }
    
    setCreating(true);
    try {
      await createTeamMember(newMember.email, newMember.password, newMember.name, newMember.role);
      toast.success(`${newMember.name} added as ${newMember.role}`);
      setNewMember({ name: '', email: '', password: '', role: 'user' });
      setShowAddForm(false);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team member');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (member) => {
    if (member.id === currentUser?.id) {
      toast.error("You can't delete yourself");
      return;
    }
    
    const confirmMsg = member.role === 'admin' 
      ? `Remove ADMIN ${member.name}? They will lose all admin privileges.`
      : `Remove ${member.name} from the team?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await deleteTeamMember(member.id);
      toast.success(`${member.name} removed`);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove team member');
    }
  };

  const handleRoleChange = async (member, newRole) => {
    if (member.id === currentUser?.id) {
      toast.error("You can't change your own role");
      return;
    }
    
    try {
      await updateUserRole(member.id, newRole);
      toast.success(`${member.name} is now ${newRole === 'admin' ? 'an Admin' : 'a User'}`);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change role');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Users className="w-5 h-5 text-accent" />
            Team Management
          </DialogTitle>
          <DialogDescription>
            Add, manage, and change roles for your team members
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-accent" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Team Members List */}
            <ScrollArea className="h-[280px] pr-4">
              <div className="space-y-2">
                {members.map((member) => {
                  const isCurrentUser = member.id === currentUser?.id;
                  return (
                    <div 
                      key={member.id} 
                      className={`flex items-center justify-between p-3 rounded-lg ${isCurrentUser ? 'bg-accent/10 border border-accent/30' : 'bg-muted/30'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${member.role === 'admin' ? 'bg-accent/20' : 'bg-muted'}`}>
                          {member.role === 'admin' ? (
                            <Shield className="w-5 h-5 text-accent" />
                          ) : (
                            <User className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm flex items-center gap-2">
                            {member.name}
                            {isCurrentUser && <span className="text-[10px] text-accent">(You)</span>}
                          </p>
                          <p className="text-xs text-muted-foreground">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Role Selector */}
                        <Select 
                          value={member.role} 
                          onValueChange={(val) => handleRoleChange(member, val)}
                          disabled={isCurrentUser}
                        >
                          <SelectTrigger className="h-8 w-24 text-xs" data-testid={`role-select-${member.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">
                              <div className="flex items-center gap-1">
                                <Shield className="w-3 h-3" /> Admin
                              </div>
                            </SelectItem>
                            <SelectItem value="user">
                              <div className="flex items-center gap-1">
                                <User className="w-3 h-3" /> User
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {/* Delete Button - can delete anyone except yourself */}
                        {!isCurrentUser && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDelete(member)}
                            data-testid={`delete-user-${member.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            {/* Add Member Form */}
            {showAddForm ? (
              <div className="space-y-3 p-4 border rounded-lg">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    placeholder="John Smith"
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    data-testid="new-member-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="john@company.com"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    data-testid="new-member-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    placeholder="Minimum 6 characters"
                    value={newMember.password}
                    onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                    data-testid="new-member-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={newMember.role} onValueChange={(val) => setNewMember({ ...newMember, role: val })}>
                    <SelectTrigger data-testid="new-member-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" /> User (Sales Rep)
                        </div>
                      </SelectItem>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4" /> Admin (Full Access)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowAddForm(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreate} 
                    disabled={creating}
                    className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground"
                    data-testid="create-member-btn"
                  >
                    {creating ? 'Adding...' : 'Add Member'}
                  </Button>
                </div>
              </div>
            ) : (
              <Button 
                onClick={() => setShowAddForm(true)}
                className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
                data-testid="show-add-form-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Team Member
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
